import { useState, useEffect, useCallback } from 'react';
import { purchaseOrdersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialPurchaseOrders } from '../data/purchaseOrders';
import type { PurchaseOrder, PurchaseOrderItem, InventoryItem } from '../types';

function normalizePoItem(
  raw: Partial<PurchaseOrderItem> & { inventoryItemId?: number },
  inventory: InventoryItem[],
): PurchaseOrderItem {
  const inv =
    raw.inventoryItemId != null ? inventory.find((i) => i.id === raw.inventoryItemId) : undefined;
  const productName =
    raw.productName?.trim() ||
    inv?.name ||
    (raw.inventoryItemId != null ? `Item #${raw.inventoryItemId}` : 'Unnamed item');
  return {
    lineId: raw.lineId || `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    inventoryItemId: raw.inventoryItemId,
    productName,
    unit: raw.unit ?? inv?.unit,
    category: raw.category ?? inv?.category,
    quantity: Math.max(1, raw.quantity ?? 1),
    costPerUnit: raw.costPerUnit ?? 0,
  };
}

export function normalizePurchaseOrder(po: PurchaseOrder, inventory: InventoryItem[]): PurchaseOrder {
  return {
    ...po,
    items: (po.items || []).map((item) =>
      normalizePoItem(item as Partial<PurchaseOrderItem> & { inventoryItemId?: number }, inventory),
    ),
  };
}

interface UsePurchaseOrdersReturn {
  purchaseOrders: PurchaseOrder[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (po: Omit<PurchaseOrder, 'id'>) => Promise<PurchaseOrder>;
  update: (id: string, updates: Partial<PurchaseOrder>) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const usePurchaseOrders = (
  enabled = true,
  inventory: InventoryItem[] = [],
): UsePurchaseOrdersReturn => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>(initialPurchaseOrders);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeList = useCallback(
    (orders: PurchaseOrder[]) => orders.map((po) => normalizePurchaseOrder(po, inventory)),
    [inventory],
  );

  const fetchOrders = useCallback(async () => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setPurchaseOrders(normalizeList(initialPurchaseOrders));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await purchaseOrdersService.getAll();
      setPurchaseOrders(normalizeList(data));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load purchase orders';
      setError(errorMessage);
      console.error('Error fetching purchase orders:', err);
      setPurchaseOrders(normalizeList(initialPurchaseOrders));
    } finally {
      setLoading(false);
    }
  }, [normalizeList]);

  useEffect(() => {
    if (!enabled) return;
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchOrders();
      return;
    }
    setLoading(true);
    const unsubscribe = purchaseOrdersService.subscribe((updated) => {
      setPurchaseOrders(normalizeList(updated));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [enabled, fetchOrders, normalizeList]);

  const create = useCallback(
    async (po: Omit<PurchaseOrder, 'id'>): Promise<PurchaseOrder> => {
      const dataSource = getDataSource();
      const normalized = normalizePurchaseOrder(
        { ...po, id: '', dateCreated: po.dateCreated || Date.now() },
        inventory,
      );
      if (dataSource === 'mock') {
        const newPo: PurchaseOrder = {
          ...normalized,
          id: `PO-${Date.now()}`,
          dateCreated: normalized.dateCreated || Date.now(),
        };
        setPurchaseOrders((prev) => [newPo, ...prev]);
        return newPo;
      }
      setError(null);
      const { id: _id, ...payload } = normalized;
      const created = await purchaseOrdersService.create(payload);
      const full = normalizePurchaseOrder(created, inventory);
      setPurchaseOrders((prev) => [full, ...prev.filter((p) => p.id !== full.id)]);
      return full;
    },
    [inventory, normalizeList],
  );

  const update = useCallback(
    async (id: string, updates: Partial<PurchaseOrder>): Promise<void> => {
      const dataSource = getDataSource();
      if (dataSource === 'mock') {
        setPurchaseOrders((prev) =>
          prev.map((p) =>
            p.id === id ? normalizePurchaseOrder({ ...p, ...updates }, inventory) : p,
          ),
        );
        return;
      }
      setError(null);
      await purchaseOrdersService.update(id, updates);
      setPurchaseOrders((prev) =>
        prev.map((p) => (p.id === id ? normalizePurchaseOrder({ ...p, ...updates }, inventory) : p)),
      );
    },
    [inventory],
  );

  const remove = useCallback(async (id: string): Promise<void> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setPurchaseOrders((prev) => prev.filter((p) => p.id !== id));
      return;
    }
    setError(null);
    await purchaseOrdersService.delete(id);
    setPurchaseOrders((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return {
    purchaseOrders,
    loading,
    error,
    refresh: fetchOrders,
    create,
    update,
    remove,
  };
};
